import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { registerVolunteer } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const commodityOptions = [
  'Musikverein',
  'Kameradschaftsbund'
];

interface FormErrors {
  [key: string]: string;
}

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { setAuthState } = useAuth();
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phoneNumber: '',
    commodity: '',
    otherCommodity: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const validate = () => {
    const newErrors: FormErrors = {};
    if (!formData.firstname) newErrors.firstname = 'Vorname ist erforderlich';
    if (!formData.lastname) newErrors.lastname = 'Nachname ist erforderlich';
    if (!formData.email) newErrors.email = 'E-Mail ist erforderlich';
    if (formData.commodity === 'Sonstige' && !formData.otherCommodity) {
      newErrors.otherCommodity = 'Bitte geben Sie Ihre Organisation an';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    try {      const response = await registerVolunteer({
        name: `${formData.firstname.trim()} ${formData.lastname.trim()}`.trim(),
        email: formData.email,
        phoneNumber: formData.phoneNumber || undefined,
        commodity: formData.commodity === 'Sonstige' 
          ? formData.otherCommodity 
          : formData.commodity,
      });
        setAuthState({
        isAuth: true,
        isAdmin: false,
        token: response.volunteer.accessToken || null,
        volunteer: response.volunteer
      });
      setSuccessMessage('Registrierung erfolgreich! Sie können sich nun mit der erhaltenen E-Mail anmelden.');
      setShowSuccessDialog(true);
      
      // Reset form
      setFormData({
        firstname: '',
        lastname: '',
        email: '',
        phoneNumber: '',
        commodity: '',
        otherCommodity: '',
      });
      
    } catch (err: any) {
      console.error('Registration failed:', err);
      if (err.response?.data?.error === 'Email already registered') {
        setErrors({
          email: 'Diese E-Mail-Adresse ist bereits registriert. Bitte verwenden Sie stattdessen den Login.'
        });
      } else {
        setErrors({ 
          submit: err.response?.data?.message || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es später erneut.'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDialogClose = () => {
    setShowSuccessDialog(false);
    navigate('/login');
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" gutterBottom align="center">
            Als Helfer registrieren
          </Typography>
          <Typography variant="body1" paragraph align="center">
            Erstellen Sie ein Konto, um sich für Dienste bei der Strudengauer Messe anzumelden
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="firstname"
              label="Vorname"
              name="firstname"
              autoComplete="given-name"
              autoFocus
              value={formData.firstname}
              onChange={handleInputChange}
              error={!!errors.firstname}
              helperText={errors.firstname}
              disabled={isSubmitting}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="lastname"
              label="Nachname"
              name="lastname"
              autoComplete="family-name"
              value={formData.lastname}
              onChange={handleInputChange}
              error={!!errors.lastname}
              helperText={errors.lastname}
              disabled={isSubmitting}
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="E-Mail"
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleInputChange}
              error={!!errors.email}
              helperText={errors.email}
              disabled={isSubmitting}
            />
            
            <TextField
              margin="normal"
              fullWidth
              id="phoneNumber"
              label="Telefonnummer (optional)"
              name="phoneNumber"
              autoComplete="tel"
              value={formData.phoneNumber}
              onChange={handleInputChange}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
              disabled={isSubmitting}
            />
            
            <FormControl fullWidth margin="normal">
              <InputLabel id="commodity-label">Organisation</InputLabel>
              <Select
                labelId="commodity-label"
                id="commodity"
                name="commodity"
                value={formData.commodity}
                onChange={handleSelectChange}
                label="Organisation"
                disabled={isSubmitting}
              >
                <MenuItem value="">
                  <em>Keine</em>
                </MenuItem>
                {commodityOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {formData.commodity === 'Sonstige' && (
              <TextField
                margin="normal"
                fullWidth
                id="otherCommodity"
                label="Andere Organisation"
                name="otherCommodity"
                value={formData.otherCommodity}
                onChange={handleInputChange}
                error={!!errors.otherCommodity}
                helperText={errors.otherCommodity}
                disabled={isSubmitting}
              />
            )}
            
            {errors.submit && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errors.submit}
              </Alert>
            )}
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3, mb: 2 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : 'Registrieren'}
            </Button>
            
            <Button
              fullWidth
              component="a"
              onClick={() => navigate('/login')}
              sx={{ textAlign: 'center' }}
            >
              Bereits registriert? Anmelden
            </Button>
          </Box>
        </Paper>
      </Box>
      
      <Dialog open={showSuccessDialog} onClose={handleDialogClose}>
        <DialogTitle>Registrierung erfolgreich</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {successMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary" autoFocus>
            Zum Login
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RegisterPage;
